import { ConfigApi, FetchApi, IdentityApi } from '@backstage/core-plugin-api';
import { GithubRunnersApi } from './githubRunnersApi';
import {
  GithubRunner,
  GithubRunnerDownloadInformation,
  GithubRunnerGroup,
  GithubRunnerRegistrationToken,
  GithubRunners,
  Tag,
} from './types';

export class GithubRunnersClient implements GithubRunnersApi {
  private readonly identityApi: IdentityApi;
  private readonly configApi: ConfigApi;
  private readonly fetchApi: FetchApi;
  private readonly baseUrl: string;
  private readonly orgName: string;

  constructor(options: {
    identityApi: IdentityApi;
    configApi: ConfigApi;
    fetchApi: FetchApi;
  }) {
    this.identityApi = options.identityApi;
    this.configApi = options.configApi;
    this.fetchApi = options.fetchApi;
    this.baseUrl = this.configApi.getString('backend.baseUrl');
    this.orgName = this.configApi.getString('organization.name');
  }

  async getGroups(): Promise<Set<string>> {
    const identity = await this.identityApi.getBackstageIdentity();
    const refs = identity.ownershipEntityRefs.filter(ref =>
      ref.includes('group'),
    );
    const groups = refs?.map(g => g.split('/').pop() || '');
    return new Set(groups);
  }

  async getRunnerGroups(): Promise<GithubRunnerGroup[]> {
    const url = `${this.baseUrl}/api/proxy/github/orgs/${this.orgName}/actions/runner-groups`;
    const belongsTo = await this.getGroups();
    const response = await this.fetchApi.fetch(url);
    const group = await response.json();
    const default_group: GithubRunnerGroup = group.runner_groups.find(
      (g: GithubRunnerGroup) => !g.inherited && g.default,
    );
    const team_groups: GithubRunnerGroup[] = group.runner_groups.filter(
      (g: GithubRunnerGroup) =>
        !g.inherited && belongsTo.has(g.name.toLowerCase()),
    );
    return team_groups.concat(default_group);
  }

  async getRunners(runnerGroupId: string): Promise<GithubRunners> {
    const url = `${this.baseUrl}/api/proxy/github/orgs/${this.orgName}/actions/runner-groups/${runnerGroupId}/runners?per_page=100`;
    return this.fetchRunnersWithPagination(url);
  }

  async getRunner(runnerId: string): Promise<GithubRunner> {
    const url = `${this.baseUrl}/api/proxy/github/orgs/${this.orgName}/actions/runners/${runnerId}`;
    const response = await this.fetchApi.fetch(url, { cache: 'no-store' });
    return response.json();
  }

  async getRunnerDownloads(): Promise<GithubRunnerDownloadInformation[]> {
    const url = `${this.baseUrl}/api/proxy/github/orgs/${this.orgName}/actions/runners/downloads`;
    const response = await this.fetchApi.fetch(url);
    return response.json();
  }

  async getRegistrationToken(): Promise<GithubRunnerRegistrationToken> {
    const url = `${this.baseUrl}/api/proxy/github/orgs/${this.orgName}/actions/runners/registration-token`;
    const response = await this.fetchApi.fetch(url, {
      method: 'POST',
    });
    return response.json();
  }

  async getRemovalToken(): Promise<GithubRunnerRegistrationToken> {
    const url = `${this.baseUrl}/api/proxy/github/orgs/${this.orgName}/actions/runners/remove-token`;
    const response = await this.fetchApi.fetch(url, {
      method: 'POST',
    });
    return response.json();
  }

  async saveRunnerToGroup(id: string, group: string): Promise<boolean> {
    const url = `${this.baseUrl}/api/proxy/github/orgs/${this.orgName}/actions/runner-groups/${group}/runners/${id}`;
    const response = await this.fetchApi.fetch(url, {
      method: 'PUT',
    });
    return response.ok;
  }

  async saveRunnerTags(id: string, tags: Tag[]): Promise<boolean> {
    const updatedTags = tags
      .filter(tag => tag.type !== 'read-only')
      .map(tag => tag.name);
    const url = `${this.baseUrl}/api/proxy/github/orgs/${this.orgName}/actions/runners/${id}/labels`;
    const response = await this.fetchApi.fetch(url, {
      method: 'POST',
      body: JSON.stringify({ labels: updatedTags }),
    });
    return response.ok;
  }

  async deleteRunnerTags(id: string): Promise<boolean> {
    const url = `${this.baseUrl}/api/proxy/github/orgs/${this.orgName}/actions/runners/${id}/labels`;
    const response = await this.fetchApi.fetch(url, {
      method: 'DELETE',
    });
    return response.ok;
  }

  private async fetchRunnersWithPagination(
    url: string,
  ): Promise<GithubRunners> {
    const runners: GithubRunners = { total_count: 0, runners: [] };

    let nextUrl: string | null = url;

    while (nextUrl) {
      const response = await this.fetchApi.fetch(nextUrl, {
        cache: 'no-cache',
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch data: ${response.statusText}`);
      }

      const data: GithubRunners = await response.json();

      runners.total_count = data.total_count;
      runners.runners.push(...data.runners);

      nextUrl = this.parseNextLink(response.headers.get('Link'));
    }

    return runners;
  }

  private parseNextLink(linkHeader: string | null): string | null {
    if (!linkHeader) return null;

    // Split the Link header into its parts and look for the 'next' link
    const links = linkHeader.split(',').map(part => part.trim());

    for (const link of links) {
      const [url, rel] = link.split(';');
      if (rel.includes('next')) {
        // Remove brackets around endpoint
        const nextUrl = url.trim().slice(1, -1);
        // Return the endpoint with the proxy baseUrl
        return nextUrl.replace(
          'https://api.github.com',
          `${this.baseUrl}/api/proxy/github`,
        );
      }
    }
    return null;
  }
}
